import React from "react";
import {
    Backdrop,
    Box,
    Dialog,
    IconButton,
    Typography,
} from "@mui/material";
import CloseRoundedIcon from "@mui/icons-material/CloseRounded";

import { ds } from "../../styles/designSystem";
import LoadingDots from "./LoadingDots";
import ActionButton from "./ActionButton";

/**
 * 폼(저장/취소) 패턴 공통 모달.
 *
 * <p>{@code DetailModal} 의 형제 컴포넌트. 결재(CreateApprovalModal)·메일(MailWriteModal) 의
 * 디자인 패턴을 기준으로 추출한 표준 — 사내 모든 폼 모달은 이 컴포넌트를 통해 동일한
 * 시각 톤(헤더 borderBottom, 푸터 borderTop + 살짝 다른 배경, 둥글지 않은 단정한 버튼)을
 * 유지한다.
 *
 * <h3>표준 사양</h3>
 * <ul>
 *   <li>Dialog 모서리: borderRadius=2 (16px), 최대 높이 92vh</li>
 *   <li>헤더: bgcolor #fff, borderBottom #e2e8f0, px=2.5 py=1.5,
 *       제목 fontSize=15 fontWeight=700 color=#0f172a, 부제 fontSize=12 color=#64748b</li>
 *   <li>본문: bgcolor #fff, px=2.5 py=2, scrollable=true 면 flex:1 + overflow:auto</li>
 *   <li>푸터: bgcolor #fafbfc, borderTop #e2e8f0, px=2.5 py=1.5</li>
 *   <li>저장 버튼: contained, borderRadius=1.5, textTransform=none, primary 색상</li>
 *   <li>취소 버튼: text, color=#64748b, textTransform=none</li>
 *   <li>저장 중: 자동 Backdrop + LoadingDots 표시</li>
 * </ul>
 */
/**
 * @param {"primary"|"danger"} saveColor 저장 버튼 색상.
 *   primary(기본) = 파랑(brand). danger = 삭제 등 위험 작업용 빨강.
 */
export default function FormModal({
    open,
    onClose,
    title,
    subtitle,
    maxWidth = "sm",
    onSave,
    saving = false,
    saveLabel = "저장",
    cancelLabel = "취소",
    saveDisabled = false,
    saveColor = "primary",
    scrollable = true,
    headerExtras,
    footerExtras,
    children,
}) {
    const saveColors = saveColor === "danger"
        ? { bg: "#dc2626", bgHover: "#b91c1c" }
        : { bg: ds.colors.primary, bgHover: ds.colors.primaryDark };

    const handleClose = (_, reason) => {
        if (saving) return;
        if (reason === "backdropClick" || reason === "escapeKeyDown") return;
        onClose?.();
    };

    return (
        <>
            <Dialog
                open={Boolean(open)}
                onClose={handleClose}
                disableEscapeKeyDown
                disableRestoreFocus
                fullWidth
                maxWidth={maxWidth}
                PaperProps={{
                    sx: {
                        borderRadius: 1.5,
                        maxHeight: "92vh",
                        bgcolor: "#fff",
                        display: "flex",
                        flexDirection: "column",
                        overflow: "hidden",
                    },
                }}
            >
                {/* 헤더 */}
                <Box
                    sx={{
                        px: 2.5,
                        py: 1.5,
                        bgcolor: "#fff",
                        borderBottom: "1px solid #e2e8f0",
                        display: "flex",
                        alignItems: "center",
                        gap: 1,
                        flexShrink: 0,
                    }}
                >
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                        <Typography
                            noWrap
                            sx={{ fontSize: 15, fontWeight: 700, color: "#0f172a", lineHeight: 1.3 }}
                            title={typeof title === "string" ? title : undefined}
                        >
                            {title}
                        </Typography>
                        {subtitle && (
                            <Typography
                                noWrap
                                sx={{ fontSize: 12, fontWeight: 600, color: "#64748b", mt: 0.2 }}
                                title={typeof subtitle === "string" ? subtitle : undefined}
                            >
                                {subtitle}
                            </Typography>
                        )}
                    </Box>

                    {headerExtras}

                    <IconButton
                        size="small"
                        onClick={() => !saving && onClose?.()}
                        disabled={saving}
                        sx={{ color: "#64748b" }}
                    >
                        <CloseRoundedIcon sx={{ fontSize: 20 }} />
                    </IconButton>
                </Box>

                {/* 본문 */}
                <Box
                    sx={{
                        px: 2.5,
                        py: 2,
                        bgcolor: "#fff",
                        ...(scrollable
                            ? { flex: 1, overflow: "auto", minHeight: 0 }
                            : {}),
                    }}
                >
                    {children}
                </Box>

                {/* 푸터 */}
                <Box
                    sx={{
                        px: 2.5,
                        py: 1.5,
                        borderTop: "1px solid #e2e8f0",
                        display: "flex",
                        alignItems: "center",
                        gap: 1,
                        bgcolor: "#fafbfc",
                        flexShrink: 0,
                    }}
                >
                    {footerExtras}
                    <Box sx={{ flex: 1 }} />

                    <ActionButton
                        variant="cancel"
                        onClick={() => !saving && onClose?.()}
                        disabled={saving}
                        label={cancelLabel}
                    />

                    {onSave && (
                        <ActionButton
                            variant="save"
                            onClick={onSave}
                            loading={saving}
                            disabled={saveDisabled}
                            label={saveLabel}
                            sx={{
                                minWidth: 88,
                                ...(saveColor === "danger" && {
                                    bgcolor: saveColors.bg,
                                    "&:hover": { bgcolor: saveColors.bgHover },
                                }),
                                "&.Mui-disabled": { bgcolor: "#e2e8f0", color: "#94a3b8" },
                            }}
                        />
                    )}
                </Box>
            </Dialog>

            {/* 저장 중 backdrop */}
            <Backdrop
                open={Boolean(saving)}
                sx={{ zIndex: (t) => t.zIndex.modal + 10, bgcolor: "rgba(255,255,255,0.85)" }}
            >
                <LoadingDots label="저장하고 있어요" />
            </Backdrop>
        </>
    );
}
